import { FieldValues, FormProvider, SubmitHandler } from "react-hook-form";
import { useCustomMutation, useInvalidate } from "@refinedev/core";
import { ShipmentPrice } from "../../types";
import { useTranslation } from "../../../../../hooks/useTranslation";
import { useForm } from "@refinedev/react-hook-form";
import { EditablePriceRow } from "./EditablePriceRow";
import { ReadonlyPriceRow } from "./ReadonlyPriceRow";

type PriceRowProps = {
  price: ShipmentPrice;
  isEditing: boolean;
  onEditStart: () => void;
  onEditStop: (_savedData?: ShipmentPrice) => void;
  backgroundColor?: string;
};

export const PriceRow = (props: PriceRowProps) => {
  const { price, isEditing, onEditStart, onEditStop, backgroundColor } = props;

  const { translate } = useTranslation("pages.shipmentPrices.details");

  const { isLoading, mutate } = useCustomMutation();
  const invalidate = useInvalidate();

  const methods = useForm<ShipmentPrice>({
    defaultValues: price,
    mode: "onChange",
  });

  const { handleSubmit, reset } = methods;

  const onSubmit: SubmitHandler<FieldValues> = (data) => {
    mutate(
      {
        url: data.id < 0 ? "shipment-prices" : `shipment-prices/${data.id}`,
        method: data.id < 0 ? "post" : "put",
        values: data,
        successNotification: () => {
          return {
            message: translate("successUpdate"),
            type: "success",
          };
        },
      },
      {
        onSuccess: () => {
          invalidate({
            resource: "all",
            invalidates: ["all"],
          });

          handleClose(data);
        },
      }
    );
  };

  const handleClose = (data?: FieldValues) => {
    reset(data ?? price);
    onEditStop(data ? (data as ShipmentPrice) : undefined);
  };

  return (
    <FormProvider {...methods}>
      {isEditing ? (
        <EditablePriceRow
          isLoading={isLoading}
          onClose={() => handleClose()}
          onSave={handleSubmit(onSubmit)}
        />
      ) : (
        <ReadonlyPriceRow
          price={price}
          onEditStart={onEditStart}
          backgroundColor={backgroundColor}
          deletable={price.id >= 0}
        />
      )}
    </FormProvider>
  );
};
